import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import authService from '../services/authService';
import { consumeCrossTenantWarning } from '../services/tenantSession';
import equiposService from '../services/equiposService';
import { degradarAAnonimo } from '../offline/catalogos';

// Contexto de autenticación
const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Provider de autenticación
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securityWarning, setSecurityWarning] = useState(null);
  const [tenantContext, setTenantContext] = useState(authService.getCurrentTenantContext());
  const [tenantSlug, setTenantSlug] = useState(authService.getCurrentTenantSlug());
  const [tenantNombre, setTenantNombre] = useState(authService.getCurrentTenantNombre());

  // Verificar autenticación al cargar la app
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setSecurityWarning(consumeCrossTenantWarning());
        setTenantContext(authService.getCurrentTenantContext());

        if (authService.hasCrossTenantSession()) {
          authService.registerCrossTenantWarning('auth-provider-initialize');
          await authService.logout();
          setUser(null);
          setTenantSlug(null);
          setError('La sesión guardada pertenece a otro tenant. Inicie sesión nuevamente.');
          setSecurityWarning(consumeCrossTenantWarning());
          return;
        }

        const currentUser = authService.getCurrentUser();
        const isAuthenticated = authService.isAuthenticated();

        if (isAuthenticated && currentUser) {
          setUser(currentUser);
          setTenantSlug(authService.getCurrentTenantSlug());

          // El usuario guardado es una foto del momento del login. Si el
          // administrador le cambió el rol o los permisos, la sesión seguiría
          // mostrando pantallas que la API ya rechaza. Se relee en segundo
          // plano: no bloquea el arranque y si falla queda lo que había.
          authService.sincronizarUsuario().then((actualizado) => {
            if (actualizado) setUser(actualizado);
          });
        }
      } catch (err) {
        console.error('Error inicializando autenticación:', err);
        setError('Error de autenticación');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      setSecurityWarning(null);

      const { user: userData, tenant, tenantNombre: tn } = await authService.login(credentials);
      equiposService.invalidateCache();
      setUser(userData);
      setTenantSlug(tenant || null);
      setTenantNombre(tn || null);
      setTenantContext(authService.getCurrentTenantContext());

      return { success: true, user: userData };
    } catch (err) {
      const status = err.response?.status;
      let errorMessage;

      if (!err.response) {
        // Sin respuesta del servidor: problema de red o servidor caído
        errorMessage = 'No se pudo conectar con el servidor. Verificá tu conexión a internet e intentá de nuevo.';
      } else if (status >= 500) {
        // Error interno del servidor
        errorMessage = 'El servidor no está disponible en este momento. Intentá de nuevo en unos minutos. Si el problema persiste, contactá al administrador.';
      } else {
        // Errores de autenticación (401, 400, etc.)
        const data = err.response?.data;
        errorMessage =
          (Array.isArray(data?.non_field_errors) ? data.non_field_errors[0] : null) ||
          data?.detail ||
          'Usuario o contraseña incorrectos.';
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Inicia sesión canjeando el código de un solo uso del login unificado.
   * Lo usa la página /acceso al aterrizar desde el sitio comercial.
   */
  const loginConCodigo = async (code) => {
    try {
      setLoading(true);
      setError(null);
      setSecurityWarning(null);

      const { user: userData, tenant, tenantNombre: tn } = await authService.exchangeHandoff(code);
      equiposService.invalidateCache();
      setUser(userData);
      setTenantSlug(tenant || null);
      setTenantNombre(tn || null);
      setTenantContext(authService.getCurrentTenantContext());

      return { success: true, user: userData };
    } catch (err) {
      const status = err.response?.status;
      let errorMessage;
      if (!err.response) {
        errorMessage = 'No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo.';
      } else if (status >= 500) {
        errorMessage = 'El servidor no está disponible en este momento. Intentá de nuevo en unos minutos.';
      } else {
        errorMessage = err.response?.data?.detail || 'El enlace de acceso venció o ya fue utilizado.';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      equiposService.invalidateCache();
      // La copia local de la app móvil también es parte de la sesión: sin esto
      // la grilla de permisos del usuario que se va sigue habilitando
      // lubricantes y combustible para quien agarre el teléfono después.
      // No falla el logout si el almacenamiento local no está disponible.
      await degradarAAnonimo().catch(() => {});
      await authService.logout();
      setUser(null);
      setTenantSlug(null);
      setTenantContext(authService.getCurrentTenantContext());
      setError(null);
    } catch (err) {
      console.error('Error en logout:', err);
      // Continuar con el logout local aunque falle el del servidor
      setUser(null);
      setTenantSlug(null);
      setTenantContext(authService.getCurrentTenantContext());
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.register(userData);

      return { success: true, data: result };
    } catch (err) {
      const errorMessage = err.message || 'Error en el registro';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setLoading(true);
      setError(null);

      await authService.changePassword(passwordData);

      // Tras cambiar la contraseña, el usuario ya no está obligado a cambiarla.
      // Reflejarlo en el estado y en la sesión guardada para levantar el gate.
      setUser((prev) => {
        if (!prev || !prev.debe_cambiar_password) return prev;
        const updated = { ...prev, debe_cambiar_password: false };
        authService.updateStoredUser(updated);
        return updated;
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Error al cambiar la contraseña';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearSecurityWarning = () => {
    setSecurityWarning(null);
  };

  /**
   * Verifica si el usuario tiene un permiso específico.
   * @param {string} model   - Nombre del modelo en minúsculas (ej. 'equipo', 'movimientostock')
   * @param {string} action  - 'view' | 'add' | 'change' | 'delete'
   * @param {string} appLabel - App de Django (por defecto 'backend_taller')
   *
   * La grilla de permisos del usuario es la fuente de verdad absoluta.
   * permisos === null → superusuario con acceso total.
   * permisos === array → solo tiene los permisos listados.
   *
   * El `appLabel` por defecto es **`backend_taller`**, que es el `label` real
   * del AppConfig de la app (ver apps/taller/apps.py: se llama así para no
   * chocar con la app legacy `taller`). Django emite los permisos con ese
   * prefijo —`backend_taller.add_equipo`— y con ese prefijo los compara el
   * backend en `DynamicModelPermission`.
   *
   * Antes el default era `'taller'`, que no coincide con ninguno de los
   * permisos que el servidor emite: para cualquier usuario que no fuera
   * superusuario, `hasPermission` devolvía false siempre. El efecto visible era
   * que pantallas y botones que la API sí habilitaba —cargar lubricantes,
   * despachar combustible, editar— sólo aparecían para superusuarios. Se
   * arrastraba parcheado a mano con `|| hasPermission(..., 'backend_taller')`
   * en un puñado de archivos; esos parches ya no hacen falta.
   */
  const hasPermission = useCallback((model, action, appLabel = 'backend_taller') => {
    if (!user) return false;
    if (user.permisos === null) return true;
    if (!Array.isArray(user.permisos)) return false;
    return user.permisos.includes(`${appLabel}.${action}_${model}`);
  }, [user]);

  const value = {
    user,
    tenantSlug,
    tenantNombre,
    tenantContext,
    loading,
    error,
    securityWarning,
    isAuthenticated: !!user,
    debeCambiarPassword: !!(user && user.debe_cambiar_password),
    login,
    loginConCodigo,
    logout,
    register,
    changePassword,
    clearError,
    clearSecurityWarning,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
